import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Search, Download, Eye, RefreshCw } from "lucide-react";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { exportToCSV } from "@/lib/export-utils";
import UserStatsCards from "@/components/admin/users/UserStatsCards";
import UserCharts from "@/components/admin/users/UserCharts";
import UserDetailsDialog from "@/components/admin/users/UserDetailsDialog";

const roleLabels: Record<string, string> = {
  admin: "مدير حضانة",
  teacher: "معلم",
  parent: "ولي أمر",
  super_admin: "مشرف عام",
  support: "دعم فني",
  accountant: "محاسب",
  developer: "مطوّر",
  call_center: "مركز اتصال",
  supervisor: "مشرف",
};

const roleColors: Record<string, string> = {
  admin: "bg-blue-100 text-blue-800",
  teacher: "bg-green-100 text-green-800",
  parent: "bg-purple-100 text-purple-800",
  super_admin: "bg-amber-100 text-amber-800",
  support: "bg-cyan-100 text-cyan-800",
  accountant: "bg-pink-100 text-pink-800",
  developer: "bg-indigo-100 text-indigo-800",
  call_center: "bg-teal-100 text-teal-800",
  supervisor: "bg-orange-100 text-orange-800",
};

const AdminUsers = () => {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [nurseryFilter, setNurseryFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*, nurseries(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const { data: roles } = await supabase.from("user_roles").select("*");
      const rolesMap = new Map<string, string[]>();
      roles?.forEach((r) => {
        const existing = rolesMap.get(r.user_id) || [];
        existing.push(r.role);
        rolesMap.set(r.user_id, existing);
      });

      return profiles?.map((p) => ({
        ...p,
        roles: rolesMap.get(p.user_id) || [],
      })) || [];
    },
  });

  const nurseryOptions = useMemo(() => {
    if (!users) return [];
    const set = new Set<string>();
    users.forEach((u: any) => {
      if (u.nurseries?.name) set.add(u.nurseries.name);
    });
    return Array.from(set).sort();
  }, [users]);

  const filtered = useMemo(() => {
    if (!users) return [];
    return users.filter((u: any) => {
      const matchSearch = !search ||
        u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        u.phone?.includes(search) ||
        u.user_id?.includes(search);
      const matchRole = roleFilter === "all" || u.roles.includes(roleFilter);
      const matchNursery = nurseryFilter === "all" ||
        (nurseryFilter === "none" ? !u.nurseries?.name : u.nurseries?.name === nurseryFilter);
      return matchSearch && matchRole && matchNursery;
    });
  }, [users, search, roleFilter, nurseryFilter]);

  const stats = useMemo(() => {
    if (!users) return { total: 0, admins: 0, teachers: 0, parents: 0, superAdmins: 0 };
    return {
      total: users.length,
      admins: users.filter((u: any) => u.roles.includes("admin")).length,
      teachers: users.filter((u: any) => u.roles.includes("teacher")).length,
      parents: users.filter((u: any) => u.roles.includes("parent")).length,
      superAdmins: users.filter((u: any) => u.roles.includes("super_admin")).length,
    };
  }, [users]);

  const handleExport = () => {
    if (!filtered?.length) return;
    exportToCSV(
      filtered.map((u: any) => ({
        الاسم: u.full_name || "",
        الهاتف: u.phone || "",
        الحضانة: u.nurseries?.name || "",
        الأدوار: u.roles.map((r: string) => roleLabels[r] || r).join("، "),
        "تاريخ التسجيل": format(new Date(u.created_at), "yyyy-MM-dd"),
      })),
      "users-export.csv"
    );
  };

  const openDetails = (user: any) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">المستخدمون</h1>
          <p className="text-muted-foreground text-sm">عرض وإدارة جميع المستخدمين المسجلين في المنصة</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 ml-1" />
            تحديث
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered?.length}>
            <Download className="w-4 h-4 ml-1" />
            تصدير CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <UserStatsCards {...stats} />

      {/* Charts */}
      {users && users.length > 0 && <UserCharts users={users} />}

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <CardTitle className="text-base">المستخدمون ({filtered?.length || 0})</CardTitle>
            </div>
            <div className="flex-1" />
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:flex-none md:w-56">
                <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input placeholder="بحث بالاسم أو الهاتف..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="الدور" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأدوار</SelectItem>
                  {Object.entries(roleLabels).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={nurseryFilter} onValueChange={setNurseryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="الحضانة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحضانات</SelectItem>
                  <SelectItem value="none">بدون حضانة</SelectItem>
                  {nurseryOptions.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="mr-2 text-muted-foreground">جاري التحميل...</span>
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>المستخدم</TableHead>
                    <TableHead>الهاتف</TableHead>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>الأدوار</TableHead>
                    <TableHead>تاريخ التسجيل</TableHead>
                    <TableHead className="w-16">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered?.map((user: any, idx: number) => (
                    <TableRow key={user.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetails(user)}>
                      <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                            {user.full_name?.charAt(0) || "؟"}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{user.full_name || "بدون اسم"}</p>
                            <p className="text-xs text-muted-foreground">{user.user_id?.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell dir="ltr" className="text-sm">{user.phone || "—"}</TableCell>
                      <TableCell className="text-sm">{(user as any).nurseries?.name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {user.roles.length ? user.roles.map((r: string) => (
                            <span key={r} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${roleColors[r] || "bg-muted"}`}>
                              {roleLabels[r] || r}
                            </span>
                          )) : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{format(new Date(user.created_at), "dd MMM yyyy", { locale: ar })}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openDetails(user); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filtered?.length && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        لا يوجد مستخدمون مطابقون للبحث
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <UserDetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} user={selectedUser} />
    </div>
  );
};

export default AdminUsers;
